import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const customerAdapter = createEntityAdapter({
    selectId: (customer) => customer._id,
    sortComparer: (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
});

// Fetch all customers
export const fetchCustomerData = createAsyncThunk(
    "customer/fetch",
    async (_, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/admin/customer/getAllCustomers",
                method: "GET",
            });

            console.log("Fetch CustomerData Response:", response);

            if (!response.success) {
                return rejectWithValue(response.message || "Failed to fetch Customer data.");
            }

            return response.data.customers;
        } catch ({ error }) {
            console.error("Error fetching customer data:", error);
            return rejectWithValue(error.message || "An unexpected error occurred while fetching customer data.");
        }
    }
);

// Fetch KYC pending data
export const fetchKycPendingData = createAsyncThunk(
    "KycPending/fetch",
    async (_, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "admin/getKycRequests",
                method: "GET",
            });

            console.log("Fetch Kyc Response:", response);

            if (!response.success) {
                return rejectWithValue(response.message || "Failed to fetch KYC data.");
            }

            return response.data;
        } catch ({ error }) {
            console.error("Error fetching KYC data:", error.message);
            return rejectWithValue(error.message || "An unexpected error occurred while fetching KYC data.");
        }
    }
);

// Fetch Customer KYC Details by userId
export const fetchCustomerKycDetails = createAsyncThunk(
    "customer/fetchKycDetails",
    async ({ userId }, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/admin/customer/getUserKycDetails",
                method: "POST",
                body: { userId }, // 🔹 Ensure body is passed correctly
            });

            console.log("Fetch Customer KYC Response:", response);

            if (!response.success) {
                return rejectWithValue(response.message || "Failed to fetch KYC details.");
            }

            return response.data; // 🔹 Returning customer data
        } catch (error) {
            console.error("Error fetching KYC data:", error.message);
            return rejectWithValue(error.message || "An unexpected error occurred.");
        }
    }
);
// Toggle Customer Active Status
export const customerToggle = createAsyncThunk(
    "customer/toggle",
    async ({ userId, isActive }, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/admin/customer/toggleUser",
                method: "POST",
                body: { isActive },
                params: { userId },
            });

            console.log("Customer Toggle Response:", response);

            if (!response.success) {
                return rejectWithValue(response.error.message || "Failed to toggle customer status.");
            }
            // Update the current state with the new isActive status
            // const currentCustomer = state.entities[action.payload._id];
            // if (currentCustomer) {
            //     currentCustomer.isActive = action.payload.isActive;
            // }

            return response.data;
        } catch ({ error }) {
            console.error("Error toggling customer status:", error.message);
            return rejectWithValue(error.message || "An unexpected error occurred.");
        }
    }
);

const initialState = customerAdapter.getInitialState({
    loading: false,
    error: null,
    kycDetails: null, // Store KYC details separately
});

const customerSlice = createSlice({
    name: "customer",
    initialState,
    reducers: {
        addCustomersIO: (state, action) => {
            customerAdapter.addMany(state, action.payload);
        },
        upsertCustomersIO: (state, action) => {
            customerAdapter.upsertMany(state, action.payload);
        },
        clearCustomersIO: (state) => {
            customerAdapter.removeAll(state);
            state.loading = false;
            state.error = null;
            state.kycDetails = null; // Clear KYC details
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Customer Data
            .addCase(fetchCustomerData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCustomerData.fulfilled, (state, action) => {
                customerAdapter.setAll(state, action.payload);
                state.loading = false;
            })
            .addCase(fetchCustomerData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Fetch KYC Pending Data
            .addCase(fetchKycPendingData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchKycPendingData.fulfilled, (state, action) => {
                customerAdapter.setAll(state, action.payload);
                state.loading = false;
            })
            .addCase(fetchKycPendingData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Fetch Customer KYC Details
            .addCase(fetchCustomerKycDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCustomerKycDetails.fulfilled, (state, action) => {
                customerAdapter.upsertOne(state, action.payload); // 🔹 Store KYC data properly
                state.loading = false;
            })
            .addCase(fetchCustomerKycDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Toggle Customer Status
            .addCase(customerToggle.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(customerToggle.fulfilled, (state, action) => {
                customerAdapter.updateOne(state, {
                    id: action.payload.userId, // Fixed: Using _id instead of userId
                    changes: { isActive: action.payload.isActive }
                });
                state.loading = false;
            })
            .addCase(customerToggle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

// Export selectors
export const {
    selectAll: selectAllCustomers,
    selectById: selectCustomerById,
    selectIds: selectCustomerIds,
} = customerAdapter.getSelectors((state) => state.customer);

// Export actions
export const { addCustomersIO, clearCustomersIO, upsertCustomersIO } = customerSlice.actions;
export default customerSlice.reducer;
